import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@restworld/utils-common';
import { useOrganizationsService } from '@restworld/data-services';
import { useAnalytics } from '@restworld/utils-common';

import { EmployerPageContentWrapper } from '../employer-page-content-wrapper';
import { ReferralHeader } from './referral-header';
import { ReferralHeroSection } from './referral-hero-section';
import { ReferralHowItWorks } from './referral-how-it-works';
import { ReferralWhyInvite } from './referral-why-invite';
import { ReferralFaqSection } from './referral-faq-section';
import { ReferralSuggestedVenues } from './referral-suggested-venues';
import { ReferralStickyCTA } from './referral-sticky-cta';

function ReferralPageContent() {
  const organizationsService = useOrganizationsService();
  const { trackEvent } = useAnalytics();

  // Clean funnel step for the referral page. No volatile props on purpose: the effect
  // re-runs when PostHog init flips trackEvent's identity (see docs/claude/analytics.md),
  // and any data-dependent prop would turn that re-run into a duplicate capture.
  useEffect(() => {
    trackEvent('referral_page_viewed', {});
  }, [trackEvent]);

  const { data: referralRequests = [] } = useQuery({
    queryKey: ['referral-requests'],
    queryFn: () =>
      organizationsService
        .listReferralRequests({ limit: 50, status: ['pending', 'accepted', 'invalidated'] })
        .then((res) => res.data),
    refetchOnWindowFocus: true,
  });

  const pendingCount = useMemo(() => referralRequests.filter((r) => r.status === 'pending').length, [referralRequests]);

  return (
    <PageWrapper>
      <ReferralHeader requestCount={pendingCount} referralRequests={referralRequests} />
      <ContentContainer>
        <ReferralHeroSection />
      </ContentContainer>
      <ReferralHowItWorks />
      <ReferralWhyInvite />
      <ReferralFaqSection />
      <ReferralSuggestedVenues />
      <ReferralStickyCTA />
    </PageWrapper>
  );
}

export default ReferralPageContent;

// Layout Components
const PageWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <EmployerPageContentWrapper className={cn('xl:px-10 lg:px-20 2xl:px-0 !p-0', className)} {...props} />
);

const ContentContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('w-full max-w-[720px] mx-auto flex flex-col gap-16 px-4 lg:px-0 py-10 pb-16', className)}
    {...props}
  />
);
